import React from 'react';

// The Dig Deeper text block — context blurb + the source credit on its OWN
// line directly below (web parity with iOS's DigDeeperSheet, 2026-07-30:
// "the link to the news source should be at the end of the context text" →
// same-day revision "on its own line, right below the context blurb").
//
// ONE component consumed by the ContextModal AND both results sheets, so the
// three can't drift — the same reason iOS keeps one `creditLine`.
//
// The blurb is written upstream to be SPOILER-FREE (the agent prompt forbids
// a directional read; `sanitizeContextBlurb` server-side enforces shape). This
// component renders it verbatim — no truncation, the modal scrolls.
//
// Credit anatomy: "SourceName · Jul 26", tinted + underlined when it links —
// never colour alone as the link signal (the underline is the redundant cue,
// per the house colour rule). Opens in a new tab: the web app has no in-app
// browser to return to, and losing a mid-ballot session to a news site would
// be the worst outcome.
// WF-1 (2026-08-11, iOS parity): highlight the brief's "what decides it"
// sentence IN PLACE when the server shipped it (`watchFor`, a verbatim
// substring of the blurb — the server drops paraphrases at intake). Soft
// category-neutral wash + weight, a highlighter pen rather than a hue-only
// signal. Degrades silently to the plain blurb on: no watchFor (everything
// pre-WF-1), substring not found, or a RESOLVED question — a watching
// assignment for a settled event is a stale instruction.
function renderBlurb(q) {
  const text = q.contextBlurb;
  const watch = q.watchFor;
  if (!watch || q.isResolved) return text;
  const i = text.indexOf(watch);
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark style={watchMark}>{watch}</mark>
      {text.slice(i + watch.length)}
    </>
  );
}

const watchMark = {
  background: 'color-mix(in srgb, var(--gold) 18%, transparent)',
  color: 'inherit',
  fontWeight: 600,
  borderRadius: 3,
  padding: '0 2px',
};

export default function ContextBody({ question }) {
  const q = question;
  if (!q?.contextBlurb) return null;

  const credit = q.contextSourceName
    ? q.contextSourceDate
      ? `${q.contextSourceName} · ${q.contextSourceDate}`
      : q.contextSourceName
    : q.contextURL
      ? 'Read the source'
      : null;

  return (
    <div>
      <p style={blurb}>{renderBlurb(q)}</p>
      {credit && (
        q.contextURL ? (
          <a href={q.contextURL} target="_blank" rel="noopener" style={creditLink}>
            {credit}
          </a>
        ) : (
          <span style={creditPlain}>{credit}</span>
        )
      )}
    </div>
  );
}

const blurb = {
  fontSize: 15.5,
  lineHeight: 1.55,
  margin: '0 0 10px',
  color: 'var(--primary-text)',
};
// Same type size as the blurb (iOS parity — the credit is a sentence of the
// text, not a footnote), gold + underline carrying the link-ness.
const creditLink = {
  fontSize: 15.5,
  lineHeight: 1.55,
  fontWeight: 600,
  color: 'var(--gold)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};
const creditPlain = {
  fontSize: 15.5,
  lineHeight: 1.55,
  fontWeight: 600,
  color: 'var(--secondary-text)',
};
